import { RouteTable } from './routeUtils';
import _ from 'lodash';
import { ApprovalType } from 'src/api/user/approvalApi';
import { UserRuleEntity } from 'src/store/user/userRule';
import { fxmuserMenus } from 'src/store/auth/auth';

// export const
export const MENU_PREFIX_FXM_UI = 'fxmui-';
export const MENU_PREFIX_FXM_USER_UI = 'fxmuserui-';

export const isAllowedMenu = (
  requiredMenus: string[] | undefined,
  userAllowedMenus: string[]
): boolean => {
  if (requiredMenus === undefined || requiredMenus.length === 0) {
    return true;
  }
  if (userAllowedMenus.length === 0) {
    return false;
  }
  return _.intersection(requiredMenus, userAllowedMenus).length > 0;
};

export const isUserAllowedMenu = (
  requiredMenus: string[] | undefined,
  userAllowedMenus: string[]
): boolean => {
  if (import.meta.env.VITE_APP_TYPE !== 'user') return true;

  return isAllowedMenu(requiredMenus, userAllowedMenus);
};

export const isDisabledMenu = (
  requiredMenus: string[] | undefined,
  disabledMenus: string[]
): boolean => {
  if (requiredMenus === undefined || requiredMenus.length === 0) {
    return true;
  }
  if (disabledMenus.length === 0) {
    return false;
  }
  return _.intersection(requiredMenus, disabledMenus).length > 0;
};

export const getFirstAllowedMenuPath = (
  menus: string[],
  mainPageRouteTables: RouteTable[]
): string | undefined => {
  const units =
    import.meta.env.VITE_APP_TYPE === 'user'
      ? ['fxmuserui-']
      : import.meta.env.VITE_APP_TYPE === 'manager'
      ? ['fxmui-']
      : ['fxmuserui-', 'fxmui-'];

  const relatedMenus = menus.filter((menu) => units.some((unit) => menu.includes(unit)));
  if (relatedMenus.length === 0) {
    return undefined;
  }

  // 접속 가능한 첫번째 depth의 URL 찾기
  const findRouteTables = mainPageRouteTables
    // 페이지 컴포넌트가 정의된 것을 필터링
    // 권한이 정의되지 않은(없어도 되는) 페이지이거나, 권한이 있는 페이지를 필터링
    .filter(
      ({ menu, component, adminScopes }) =>
        menu &&
        // component &&
        (!adminScopes ||
          adminScopes.length === 0 ||
          _.intersection(adminScopes, relatedMenus).length > 0)
    );

  return findRouteTables.length > 0 ? findRouteTables[0].fullPath : undefined;
};

export const userAllowedMenus = (userRule: UserRuleEntity, useProxy: boolean): string[] => {
  // approver = true && proxyApprover = true   : 결재대기, 결재 완료, 대리승인
  // approver = true && proxyApprover = false  : 결재대기, 결재 완료, 대리승인
  // approver = false && proxyApprover = true  : 결재대기, 결재 완료
  // approver = false && proxyApprover = false :
  let userMenus: string[] = fxmuserMenus;
  if (!userRule.approver && !userRule.proxyApprover) {
    userMenus = userMenus.filter(
      (menu) =>
        menu !== 'fxmuserui-approval-pending' &&
        menu !== 'fxmuserui-approval-complete' &&
        menu !== 'fxmuserui-approval-delegation'
    );
  } else if (!userRule.approver && userRule.proxyApprover) {
    userMenus = userMenus.filter((menu) => menu !== 'fxmuserui-approval-delegation');
  }

  if (!useProxy) {
    userMenus = userMenus.filter((menu) => menu !== 'fxmuserui-approval-delegation');
  }

  if (userRule.primary === undefined || userRule.primary === null || userRule.primary.length < 1) {
    userMenus = userMenus.filter((menu) => !menu.startsWith('fxmuserui-request-new'));
  } else {
    if (
      userRule.primary.filter((policy) => policy.approvalType === ApprovalType.TAKEOUT).length < 1
    ) {
      userMenus = userMenus.filter((menu) => menu !== 'fxmuserui-request-new-pc');
    }
    if (
      userRule.primary.filter((policy) => policy.approvalType === ApprovalType.DECRYPT).length < 1
    ) {
      userMenus = userMenus.filter((menu) => menu !== 'fxmuserui-request-new-decryption');
    }
    if (
      userRule.primary.filter((policy) => policy.approvalType === ApprovalType.SCREEN).length < 1
    ) {
      userMenus = userMenus.filter((menu) => menu !== 'fxmuserui-request-new-exception');
    }
    if (
      userRule.primary.filter((policy) => policy.approvalType === ApprovalType.PRINT_PERIOD)
        .length < 1
    ) {
      userMenus = userMenus.filter((menu) => menu !== 'fxmuserui-request-new-printPeriod');
    }
    if (
      userRule.primary.filter((policy) => policy.approvalType === ApprovalType.PRINT_FILE).length <
      1
    ) {
      userMenus = userMenus.filter((menu) => menu !== 'fxmuserui-request-new-printFile');
    }
    if (
      userRule.primary.filter((policy) => policy.approvalType === ApprovalType.PRINT_CLIENT)
        .length < 1
    ) {
      userMenus = userMenus.filter((menu) => menu !== 'fxmuserui-request-new-printClient');
    }
  }

  return userMenus;
};

export const userDisabledRequest = (userRule: UserRuleEntity): string[] => {
  let disabledMenus: string[] = [];
  // 사용자 > 신청하기에서 신청 유형별 비활성화 여부 체크함
  if (
    userRule.isPrimaryExceed !== undefined &&
    userRule.isPrimaryExceed !== null &&
    userRule.primary !== null &&
    userRule.primary.length > 0
  ) {
    const disabledRuleCodes = userRule.primary
      ?.map((policy) => policy.code)
      .filter((code) =>
        userRule.isPrimaryExceed === null ? false : !userRule.isPrimaryExceed[code]
      );

    disabledRuleCodes.forEach((code) => {
      const types = userRule.primary
        ?.filter((policy) => policy.code === code)
        .map((policy) => policy.approvalType);
      if (types !== undefined && types.length > 0) {
        if (types.includes(ApprovalType.TAKEOUT)) {
          disabledMenus = disabledMenus.concat(['fxmuserui-request-new-pc']);
        }
        if (types.includes(ApprovalType.SCREEN)) {
          disabledMenus = disabledMenus.concat(['fxmuserui-request-new-exception']);
        }
        if (types.includes(ApprovalType.DECRYPT)) {
          disabledMenus = disabledMenus.concat(['fxmuserui-request-new-decryption']);
        }
        if (types.includes(ApprovalType.PRINT_PERIOD)) {
          disabledMenus = disabledMenus.concat(['fxmuserui-request-new-printPeriod']);
        }
        if (types.includes(ApprovalType.PRINT_FILE)) {
          disabledMenus = disabledMenus.concat(['fxmuserui-request-new-printFile']);
        }
        if (types.includes(ApprovalType.PRINT_CLIENT)) {
          disabledMenus = disabledMenus.concat(['fxmuserui-request-new-printClient']);
        }
      }
    });
  }

  return disabledMenus;
};
